// Custom SVG service icons — minimal, monochrome, geometric.
// Each icon is a square viewBox 80x80 and inherits currentColor.

const stroke = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.25,
    strokeLinecap: "round",
    strokeLinejoin: "round",
};

const fillDot = { fill: "currentColor", stroke: "none" };

export const Icon = ({ name, className = "w-14 h-14" }) => {
    const map = {
        // 01 Premium Business Websites — layered window with grid
        site: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <rect x="8" y="14" width="64" height="52" {...stroke} />
                <line x1="8" y1="24" x2="72" y2="24" {...stroke} />
                <circle cx="14" cy="19" r="1.5" {...fillDot} />
                <circle cx="20" cy="19" r="1.5" {...fillDot} />
                <circle cx="26" cy="19" r="1.5" {...fillDot} />
                <rect x="14" y="30" width="22" height="14" {...stroke} />
                <line x1="40" y1="30" x2="66" y2="30" {...stroke} />
                <line x1="40" y1="36" x2="60" y2="36" {...stroke} />
                <line x1="40" y1="42" x2="64" y2="42" {...stroke} />
                <line x1="14" y1="50" x2="66" y2="50" {...stroke} />
                <line x1="14" y1="58" x2="50" y2="58" {...stroke} />
            </svg>
        ),
        // 02 High-Conversion Landing Pages — page with arrow
        landing: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <rect x="14" y="8" width="52" height="64" {...stroke} />
                <line x1="20" y1="18" x2="40" y2="18" {...stroke} />
                <line x1="20" y1="24" x2="48" y2="24" {...stroke} />
                <rect x="20" y="34" width="40" height="20" {...stroke} />
                <path d="M 28 64 L 52 64" {...stroke} />
                <path d="M 46 60 L 52 64 L 46 68" {...stroke} />
            </svg>
        ),
        // 03 GBP Optimization — map pin in grid
        map: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <line x1="8" y1="20" x2="72" y2="20" {...stroke} />
                <line x1="8" y1="40" x2="72" y2="40" {...stroke} />
                <line x1="8" y1="60" x2="72" y2="60" {...stroke} />
                <line x1="20" y1="8" x2="20" y2="72" {...stroke} />
                <line x1="40" y1="8" x2="40" y2="72" {...stroke} />
                <line x1="60" y1="8" x2="60" y2="72" {...stroke} />
                <path d="M 40 26 C 32 26 26 32 26 40 C 26 50 40 60 40 60 C 40 60 54 50 54 40 C 54 32 48 26 40 26 Z" {...stroke} />
                <circle cx="40" cy="40" r="4" {...fillDot} />
            </svg>
        ),
        // 04 Social Presence — connected nodes
        nodes: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <line x1="18" y1="22" x2="40" y2="40" {...stroke} />
                <line x1="62" y1="22" x2="40" y2="40" {...stroke} />
                <line x1="18" y1="58" x2="40" y2="40" {...stroke} />
                <line x1="62" y1="58" x2="40" y2="40" {...stroke} />
                <circle cx="18" cy="22" r="5" {...stroke} />
                <circle cx="62" cy="22" r="5" {...stroke} />
                <circle cx="18" cy="58" r="5" {...stroke} />
                <circle cx="62" cy="58" r="5" {...stroke} />
                <circle cx="40" cy="40" r="6" {...fillDot} />
            </svg>
        ),
        // 05 Lead Generation — funnel
        funnel: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <path d="M 12 14 L 68 14 L 50 38 L 50 58 L 30 66 L 30 38 Z" {...stroke} />
                <line x1="12" y1="22" x2="68" y2="22" {...stroke} />
                <circle cx="40" cy="50" r="2" {...fillDot} />
            </svg>
        ),
        // 06 Reputation Systems — stars + bar
        stars: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                {[10, 24, 38, 52, 66].map((x, i) => (
                    <path
                        key={i}
                        d={`M ${x} 22 L ${x + 2.5} 28 L ${x + 9} 28 L ${x + 4} 32 L ${x + 6} 39 L ${x} 35 L ${x - 6} 39 L ${x - 4} 32 L ${x - 9} 28 L ${x - 2.5} 28 Z`}
                        {...(i < 4 ? fillDot : stroke)}
                    />
                ))}
                <rect x="10" y="50" width="60" height="6" {...stroke} />
                <rect x="10" y="50" width="42" height="6" {...fillDot} />
                <text
                    x="10"
                    y="68"
                    style={{
                        fontFamily: "Satoshi, sans-serif",
                        fontSize: "8px",
                        letterSpacing: "0.2em",
                        textTransform: "uppercase",
                        fill: "currentColor",
                    }}
                >
                    4.9 / 5.0
                </text>
            </svg>
        ),
        // 07 Website Redesigns — before/after split
        redesign: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <rect x="6" y="14" width="32" height="52" {...stroke} />
                <line x1="10" y1="20" x2="22" y2="20" {...stroke} />
                <line x1="10" y1="26" x2="34" y2="26" {...stroke} />
                <line x1="10" y1="32" x2="28" y2="32" {...stroke} />
                <line x1="10" y1="40" x2="32" y2="40" {...stroke} />
                <line x1="10" y1="46" x2="22" y2="46" {...stroke} />
                <line x1="10" y1="52" x2="30" y2="52" {...stroke} />
                <path d="M 42 40 L 50 40" {...stroke} />
                <path d="M 47 36 L 50 40 L 47 44" {...stroke} />
                <rect x="52" y="14" width="22" height="52" {...stroke} />
                <rect x="56" y="20" width="14" height="14" {...fillDot} />
                <line x1="56" y1="40" x2="70" y2="40" {...stroke} />
                <line x1="56" y1="46" x2="66" y2="46" {...stroke} />
            </svg>
        ),
        // 08 Business Content Systems — editorial columns
        editorial: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <line x1="12" y1="14" x2="68" y2="14" {...stroke} />
                <text
                    x="40"
                    y="26"
                    textAnchor="middle"
                    style={{
                        fontFamily: "Cormorant Garamond, serif",
                        fontStyle: "italic",
                        fontSize: "10px",
                        fill: "currentColor",
                    }}
                >
                    Editorial
                </text>
                <line x1="12" y1="32" x2="68" y2="32" {...stroke} />
                <g>
                    {[12, 28, 44, 60].map((x) => (
                        <g key={x}>
                            <line x1={x} y1="38" x2={x + 12} y2="38" {...stroke} />
                            <line x1={x} y1="44" x2={x + 12} y2="44" {...stroke} />
                            <line x1={x} y1="50" x2={x + 10} y2="50" {...stroke} />
                            <line x1={x} y1="56" x2={x + 12} y2="56" {...stroke} />
                            <line x1={x} y1="62" x2={x + 8} y2="62" {...stroke} />
                        </g>
                    ))}
                </g>
            </svg>
        ),
        // 09 Booking & Appointment — calendar grid with marker
        calendar: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <rect x="10" y="16" width="60" height="50" {...stroke} />
                <line x1="10" y1="28" x2="70" y2="28" {...stroke} />
                <line x1="22" y1="12" x2="22" y2="20" {...stroke} />
                <line x1="58" y1="12" x2="58" y2="20" {...stroke} />
                {[0, 1, 2, 3].map((r) =>
                    [0, 1, 2, 3, 4, 5].map((c) => (
                        <circle
                            key={`${r}-${c}`}
                            cx={16 + c * 10}
                            cy={36 + r * 8}
                            r="1.4"
                            {...(r === 2 && c === 3 ? fillDot : stroke)}
                        />
                    ))
                )}
                <circle cx="46" cy="52" r="5" {...stroke} />
            </svg>
        ),
        // 10 Website Care Plans — shield + check
        shield: (
            <svg viewBox="0 0 80 80" className={className} aria-hidden>
                <path d="M 40 10 L 64 20 L 64 42 C 64 56 52 66 40 70 C 28 66 16 56 16 42 L 16 20 Z" {...stroke} />
                <path d="M 28 42 L 36 50 L 54 32" {...stroke} />
            </svg>
        ),
    };
    return map[name] || null;
};

export const ICON_BY_NUMBER = {
    "01": "site",
    "02": "landing",
    "03": "map",
    "04": "nodes",
    "05": "funnel",
    "06": "stars",
    "07": "redesign",
    "08": "editorial",
    "09": "calendar",
    "10": "shield",
};
